import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminAddBookDialog } from '@/components/admin/AdminAddBookDialog';
import { AdminBookTable } from '@/components/admin/AdminBookTable';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { FilterPill } from '@/components/filters/FilterPill';
import { FilterRail, EMPTY_FILTERS, type CatalogFilters } from '@/components/filters/FilterRail';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { adminBooksService } from '@/services/admin-books.service';
import { ApiError } from '@/services/http';
import { LABEL_TYPES, LABEL_TYPE_SINGULAR, type LabelType } from '@/constants/label-types';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * AdminDashboard — `/admin`. DESIGN.md Artboard 3 + ADMIN_DASHBOARD.md.
 *
 * Layout (parent mode):
 *   1. Admin sub-header — "Admin · Catalog" kicker, "Book Management"
 *      heading, Export CSV (placeholder) + Add Book (opens dialog).
 *   2. Stats strip — four `StatCard`s.
 *   3. Two-column body — left = toolbar + `AdminBookTable` + pagination;
 *      right = `FilterRail` + Account Management placeholder.
 *
 * URL is the source of truth for filters + page, mirroring the Catalog
 * page's convention. That means filter deep-links work, and back/forward
 * navigation works out of the box.
 *
 * Stats caveat (per CLIENT_PLAN.md §Phase 6 "Missing covers"): the server's
 * `/admin/books` returns only a page-worth of books plus an overall
 * `totalCount`, so only "Total books" is exact. Live / Pending / Missing
 * covers are derived from the visible page so they move sensibly with
 * filters; the delta copy is framed as "on this page" so we don't lie.
 * Swap to a `/admin/stats` endpoint when it exists.
 */
const PAGE_SIZE = 20;

export function AdminDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  // Committed search text — what's actually in the URL and being sent
  // to the server. The input below holds a separate `searchDraft` value
  // that flows into here on a debounced timer.
  const committedQuery = (searchParams.get('q') ?? '').trim();

  const [books, setBooks] = useState<BookView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState<LabelType | null>(null);
  const [addBookOpen, setAddBookOpen] = useState(false);
  // Bump to force a re-fetch after an admin creates a new book or does
  // something else that mutates the list out-of-band.
  const [refreshTick, setRefreshTick] = useState(0);

  // Local draft for the search input. Synced FROM the URL whenever the
  // committed query changes from elsewhere (back button, clear-all,
  // pagination). It is pushed TO the URL only when the admin submits the
  // search — pressing Enter or clicking Search — never on a timer.
  const [searchDraft, setSearchDraft] = useState(committedQuery);
  useEffect(() => {
    setSearchDraft(committedQuery);
  }, [committedQuery]);

  // Explicit commit: push the trimmed draft into the URL (which kicks the
  // fetch effect via the `committedQuery` change). Page resets to 1 since a
  // different query has a different result window. No-op when nothing changed.
  const commitSearch = useCallback(() => {
    const trimmed = searchDraft.trim();
    if (trimmed === committedQuery) return;
    writeParams({ filters, page: 1, q: trimmed }, setSearchParams);
  }, [searchDraft, committedQuery, filters, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const offset = (page - 1) * PAGE_SIZE;
    (async () => {
      try {
        const result = await adminBooksService.list({
          ...filters,
          q: committedQuery || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setBooks(result.books);
        setTotalCount(result.totalCount);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load the admin catalog.');
        setBooks([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, page, refreshTick, committedQuery]);

  // Clamp `page` if the filter change shrank the result window.
  useEffect(() => {
    if (loading) return;
    if (totalCount === 0 && page > 1) {
      writeParams({ filters, page: 1, q: committedQuery }, setSearchParams, { replace: true });
      return;
    }
    const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > lastPage) {
      writeParams({ filters, page: lastPage, q: committedQuery }, setSearchParams, {
        replace: true,
      });
    }
  }, [loading, totalCount, page, filters, committedQuery, setSearchParams]);

  const applyFilters = useCallback(
    (next: CatalogFilters) => {
      writeParams({ filters: next, page: 1, q: committedQuery }, setSearchParams);
    },
    [committedQuery, setSearchParams],
  );

  const removePill = useCallback(
    (type: LabelType, alias: string) => {
      const next: CatalogFilters = { ...filters, [type]: filters[type].filter((v) => v !== alias) };
      writeParams({ filters: next, page: 1, q: committedQuery }, setSearchParams);
    },
    [filters, committedQuery, setSearchParams],
  );

  const clearAll = useCallback(() => {
    // "Clear all" wipes the title search too — clearAll is a single
    // affordance for "show me everything", not just "drop label pills".
    writeParams({ filters: EMPTY_FILTERS, page: 1, q: '' }, setSearchParams);
  }, [setSearchParams]);

  const clearSearch = useCallback(() => {
    setSearchDraft('');
    writeParams({ filters, page: 1, q: '' }, setSearchParams);
  }, [filters, setSearchParams]);

  const activePills = useMemo(() => {
    const out: Array<{ type: LabelType; alias: string }> = [];
    for (const type of LABEL_TYPES) {
      for (const alias of filters[type]) out.push({ type, alias });
    }
    return out;
  }, [filters]);

  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(lastPage, next));
    if (clamped === page) return;
    writeParams({ filters, page: clamped, q: committedQuery }, setSearchParams);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Page-local stat aggregates. See class doc — only `totalCount` is exact.
  const liveOnPage = books.filter((b) => b.status === 'live').length;
  const pendingOnPage = books.filter((b) => b.status === 'pending').length;
  const missingCoversOnPage = books.filter((b) => !b.bookCover).length;

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-8">
        {/* ---------- Section tabs (Books / Articles) ---------- */}
        <AdminSectionTabs />

        {/* ---------- Sub-header ---------- */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Catalog</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Book Management
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              disabled
              // TODO: wire to /admin/books/export once the endpoint lands.
              title="Export CSV — coming soon"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export CSV
            </Button>
            <Button variant="primary" size="md" onClick={() => setAddBookOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              Add Book
            </Button>
          </div>
        </div>

        {/* ---------- Stats strip ---------- */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total books"
            value={totalCount.toLocaleString()}
            delta={loading ? 'Refreshing…' : 'Live + pending'}
            tone="default"
          />
          <StatCard
            label="Live"
            value={liveOnPage.toLocaleString()}
            delta={`${liveOnPage} of ${books.length} on this page`}
            tone="success"
          />
          <StatCard
            label="Pending review"
            value={pendingOnPage.toLocaleString()}
            delta={
              pendingOnPage === 0
                ? 'No pending books on this page'
                : `${pendingOnPage} awaiting review`
            }
            tone="warning"
          />
          <StatCard
            label="Missing covers"
            value={missingCoversOnPage.toLocaleString()}
            delta={
              missingCoversOnPage === 0
                ? 'All covers in place here'
                : `${missingCoversOnPage} need artwork`
            }
            tone="danger"
          />
        </div>

        {/* ---------- Body ---------- */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* --- Table column --- */}
          <div className="min-w-0 flex-1">
            {/* Title search — full-width above the toolbar. The query is
                committed only when the admin submits (presses Enter or clicks
                Search), not on a timer. The URL is the source of truth, so
                deep-linking and back/forward navigation preserve the search. */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                commitSearch();
              }}
              className="mb-3 flex items-center gap-2"
            >
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-or-slate-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Search books by title…"
                  aria-label="Search books by title"
                  maxLength={200}
                  className="h-10 w-full rounded-full border border-or-slate-200 bg-white pl-10 pr-10 text-[13px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                />
                {searchDraft.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-or-slate-400 hover:bg-or-slate-100 hover:text-or-slate-600"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
              <button
                type="submit"
                className="h-10 shrink-0 rounded-full bg-or-orange-500 px-5 text-[13px] font-semibold text-white transition hover:bg-or-orange-600 focus:outline-none focus:ring-2 focus:ring-or-orange-400/40"
              >
                Search
              </button>
            </form>

            {/* Toolbar — filter pills + range indicator. */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {activePills.length === 0 && !committedQuery ? (
                <span className="text-[12px] italic text-or-slate-500">
                  No filters applied — showing every book in the catalog.
                </span>
              ) : (
                <>
                  {activePills.map((pill) => (
                    <FilterPill
                      key={`${pill.type}:${pill.alias}`}
                      kicker={LABEL_TYPE_SINGULAR[pill.type]}
                      value={pill.alias}
                      onOpen={() => setOpenSection(pill.type)}
                      onRemove={() => removePill(pill.type, pill.alias)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-700"
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>

            <div className="mb-3 text-[12px] text-or-slate-600">
              {loading ? (
                <span>Loading books…</span>
              ) : totalCount === 0 ? (
                <span>
                  {committedQuery
                    ? `No books match "${committedQuery}".`
                    : 'No books match these filters.'}
                </span>
              ) : (
                <span>
                  Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{' '}
                  {totalCount.toLocaleString()} books
                  {committedQuery ? ` matching "${committedQuery}"` : ''}
                </span>
              )}
            </div>

            <AdminBookTable
              books={books}
              loading={loading}
              emptyLabel={
                committedQuery
                  ? `No books match "${committedQuery}".`
                  : activePills.length > 0
                    ? 'No books match these filters.'
                    : 'No books in the catalog yet.'
              }
            />

            {!loading && totalCount > PAGE_SIZE ? (
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className={cn(
                    'inline-flex h-9 items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] font-semibold text-or-slate-700 transition',
                    'hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  Previous
                </button>
                <div className="text-[12px] text-or-slate-500">
                  Page {page} of {lastPage}
                </div>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= lastPage}
                  className={cn(
                    'inline-flex h-9 items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] font-semibold text-or-slate-700 transition',
                    'hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ) : null}
          </div>

          {/* --- Sidebar column --- */}
          <div className="flex flex-col gap-4 lg:w-[300px] lg:shrink-0">
            <FilterRail
              value={filters}
              onApply={applyFilters}
              initialOpenSection={openSection}
              className="lg:sticky lg:top-24"
            />

            {/* Account management placeholder — ADMIN_DASHBOARD.md §"Account
                Management". Keeps the visual column balanced with the
                filter rail above it and sets the hook for future admin-
                user management. */}
            <div className="rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-display text-[16px] font-bold text-or-slate-900">
                Account management
              </h2>
              <p className="mt-1 text-[12px] leading-relaxed text-or-slate-600">
                Manage admin accounts, roles, or access settings.
              </p>
              <button
                type="button"
                disabled
                className="mt-4 inline-flex h-9 items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] font-semibold text-or-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
                title="Coming soon"
              >
                Manage admins →
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminAddBookDialog
        open={addBookOpen}
        onOpenChange={setAddBookOpen}
        onCreated={() => {
          // New book starts as `pending`; bumping refreshTick re-pulls the
          // current page so the row lands in the table without the admin
          // having to reload the dashboard.
          setRefreshTick((n) => n + 1);
        }}
      />
    </AppShell>
  );
}

function readFilters(searchParams: URLSearchParams): CatalogFilters {
  const out: CatalogFilters = {
    genre: [],
    topic: [],
    reading_level: [],
    interest_level: [],
    historical_event: [],
    time_period: [],
    book_series: [],
  };
  for (const type of LABEL_TYPES) {
    out[type] = searchParams.getAll(type).filter((v) => v.trim() !== '');
  }
  return out;
}

function writeParams(
  next: { filters: CatalogFilters; page: number; q?: string },
  setSearchParams: SetURLSearchParams,
  options: { replace?: boolean } = {},
) {
  const params = new URLSearchParams();
  for (const type of LABEL_TYPES) {
    for (const alias of next.filters[type]) params.append(type, alias);
  }
  if (next.page > 1) params.set('page', String(next.page));
  if (next.q !== undefined) {
    const trimmed = next.q.trim();
    if (trimmed) params.set('q', trimmed);
  }
  setSearchParams(params, options);
}
